import {
  AgenticOAuthCooldownError,
  callAgenticTool,
  completeAgenticOAuth,
  disconnectAgenticOAuth,
  fetchAgenticAccounts,
  fetchAgenticSnapshot,
  fetchAgenticStatus,
  fetchAgenticTools,
  fetchAgenticUpdates,
  patchAgenticUpdate,
  refreshAgenticSnapshot,
  startAgenticOAuth,
  type AgenticOAuthCooldown,
  type AgenticRobinhoodAccount,
  type AgenticSettings,
  type AgenticSnapshot,
  type AgenticSnapshotAccountData,
  type AgenticTokenExpiry,
  type AgenticToolDefinition,
  type AgenticUpdateItem,
} from "../api/agenticClient";
import { scopeAllowsTicker, subscribeAccountScope } from "./accountScope";
import { notifyRobinhoodStatusChanged } from "./robinhoodStatus";
import {
  emptyLedgerCatalog,
  resolveImportTargetSelection,
  type LedgerCatalogState,
} from "./ledgerBrokersSettings";
import { loadOwnLedgerImportStatus } from "./ownLedgerClient";
import { AGENTIC_SETTINGS_CHANGED_EVENT, notifyAgenticSnapshotRefreshed } from "./agenticRefreshSettings";
import {
  accountShortCellHtml,
  brokerMarkCellHtml,
  ensureLedgerMarkIndex,
  markForAccountId,
  subscribeLedgerMarks,
} from "./ledgerMarks";
import {
  bindDetailsCollapsiblePersistence,
  isCollapsibleOpen,
  setCollapsibleOpen,
} from "./collapsibleSections";
import {
  deriveRobinhoodMarginSummary,
  formatMarginPercent,
  formatMarginRate,
} from "./agenticMarginSummary";
import { notifyAgenticAccountChanged } from "./agenticPortfolioCash";
import {
  agenticUpdateToLedgerInput,
  bindImportSettings,
  formatImportMetaText,
  renderUpdatesPanel,
  updateKindLabel,
  type ImportSettingsContext,
} from "./agenticUpdatesPanel";
import {
  afterWheelLedgerChange,
  applyAgenticUpdateToLedger,
  etTodayISO,
  fetchAllLedgerTransactions,
  findLedgerTxnIdsForAgenticUpdate,
  findLedgerTxnIdsInTransactions,
  normalizeRhOptionPremium,
  type WheelLedgerTransaction,
} from "./wheelActions";
import { invalidateTransactionsSnapshot } from "./wheelTransactions";
import {
  loadOwnLedgerOpenOptions,
  loadOwnLedgerShareAvgByTicker,
} from "./ownLedgerClient";
import {
  loadPositionsSnapshot,
  normalizeTicker,
  type PositionsSnapshot,
} from "./watchlistData";
import { showToast } from "./toast";
import { cacheAgenticSettings, readCachedAgenticSettings } from "./agenticSettingsCache";

const DEFAULT_AGENTIC_SETTINGS: AgenticSettings = {
  snapshotAutoRefreshEnabled: true,
  snapshotAutoRefreshMinutes: 7,
  autoImportEnabled: false,
  autoImportLastRunAt: null,
};

const IMPORT_COLLAPSIBLE_SCOPE = "import";

/** StockForge+ held shares by normalized ticker. */
type SfQtyMap = Map<string, number>;

const AGENTIC_SELECTED_ACCOUNT_KEY = "sfe-agentic-selected-account";

function loadStoredAgenticAccount(): string | null {
  try {
    const raw = localStorage.getItem(AGENTIC_SELECTED_ACCOUNT_KEY);
    return raw?.trim() || null;
  } catch {
    return null;
  }
}

function storeAgenticAccount(accountNumber: string): void {
  try {
    localStorage.setItem(AGENTIC_SELECTED_ACCOUNT_KEY, accountNumber);
  } catch {
    /* ignore */
  }
  notifyAgenticAccountChanged();
}

function resolveSelectableAccounts(
  snapshot: AgenticSnapshot | null,
  accounts: AgenticRobinhoodAccount[],
): AgenticRobinhoodAccount[] {
  if (snapshot?.accounts?.length) return snapshot.accounts;
  return accounts;
}

function defaultAccountNumber(accounts: AgenticRobinhoodAccount[]): string | null {
  if (!accounts.length) return null;
  return accounts.find((account) => account.isDefault)?.accountNumber ?? accounts[0].accountNumber;
}

function normalizeSelectedAccount(
  accounts: AgenticRobinhoodAccount[],
  selected: string | null,
): string | null {
  if (!accounts.length) return null;
  if (selected && accounts.some((account) => account.accountNumber === selected)) {
    return selected;
  }
  const stored = loadStoredAgenticAccount();
  if (stored && accounts.some((account) => account.accountNumber === stored)) {
    return stored;
  }
  return defaultAccountNumber(accounts);
}

function renderAccountOptionLabel(account: AgenticRobinhoodAccount): string {
  const parts = [account.name];
  if (account.tradingType) parts.push(formatTradingType(account.tradingType));
  if (account.maskedAccountNumber) parts.push(account.maskedAccountNumber);
  return parts.join(" · ");
}

function renderTradingAccountSelector(
  accounts: AgenticRobinhoodAccount[],
  selectedAccountNumber: string | null,
): string {
  if (!accounts.length) return "";
  const options = accounts
    .map((account) => {
      const selected = account.accountNumber === selectedAccountNumber ? " selected" : "";
      return `<option value="${esc(account.accountNumber)}"${selected}>${esc(renderAccountOptionLabel(account))}</option>`;
    })
    .join("");
  return `
    <div class="sfe-agentic-account-select-row">
      <label class="sfe-agentic-field sfe-agentic-account-select">
        <span class="small muted">Trading account</span>
        <select data-agentic-account-select>${options}</select>
      </label>
    </div>`;
}

function snapshotAccountEntries(
  snapshot: AgenticSnapshot | null,
  accountNumber: string | null,
): Array<[string, AgenticSnapshotAccountData | undefined]> {
  if (!snapshot?.byAccount) return [];
  if (accountNumber && snapshot.byAccount[accountNumber]) {
    return [[accountNumber, snapshot.byAccount[accountNumber]]];
  }
  return Object.entries(snapshot.byAccount);
}

function formatExpiresIn(sec: number): string {
  if (sec <= 0) return "expired";
  if (sec >= 86400) {
    const days = Math.floor(sec / 86400);
    const hours = Math.ceil((sec % 86400) / 3600);
    return hours > 0 ? `${days} day${days === 1 ? "" : "s"} ${hours} hour${hours === 1 ? "" : "s"}` : `${days} day${days === 1 ? "" : "s"}`;
  }
  if (sec >= 3600) return `${Math.ceil(sec / 3600)} hour${sec >= 7200 ? "s" : ""}`;
  if (sec >= 60) return `${Math.ceil(sec / 60)} minute${sec >= 120 ? "s" : ""}`;
  return `${sec} second${sec === 1 ? "" : "s"}`;
}

function renderTokenExpiry(expiry: AgenticTokenExpiry | null): string {
  if (!expiry?.accessTokenExpiresAt) return "";
  const sec = expiry.accessTokenExpiresInSec ?? 0;
  const when =
    sec > 0
      ? `expires in ${formatExpiresIn(sec)}`
      : "expired — will refresh on next Robinhood call";
  const refreshNote = expiry.hasRefreshToken ? " Auto-refreshes when you use Agentic." : "";
  const at = formatSnapshotTime(expiry.accessTokenExpiresAt);
  return `<p class="muted small sfe-agentic-token-expiry" data-agentic-token-expiry role="status">Access token ${esc(when)} (${esc(at)}).${esc(refreshNote)}</p>`;
}

function formatRetryAfter(sec: number): string {
  if (sec >= 3600) return `${Math.ceil(sec / 3600)} hour${sec >= 7200 ? "s" : ""}`;
  if (sec >= 60) return `${Math.ceil(sec / 60)} minute${sec >= 120 ? "s" : ""}`;
  return `${sec} second${sec === 1 ? "" : "s"}`;
}

function esc(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

const PENDING_AUTH_STORAGE_KEY = "sfe.agentic.pendingAuthUrl";

function readPendingAuthUrl(): string | null {
  try {
    const value = sessionStorage.getItem(PENDING_AUTH_STORAGE_KEY)?.trim() ?? "";
    if (value.startsWith("https://") || value.startsWith("http://")) return value;
  } catch {
    // Private mode / quota.
  }
  return null;
}

function writePendingAuthUrl(url: string | null): void {
  try {
    if (url) sessionStorage.setItem(PENDING_AUTH_STORAGE_KEY, url);
    else sessionStorage.removeItem(PENDING_AUTH_STORAGE_KEY);
  } catch {
    // Private mode / quota.
  }
}

function formatJson(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}

function formatTradingType(value: string | null): string {
  if (!value) return "";
  return value.replaceAll("_", " ").replace(/\b\w/g, (char) => char.toUpperCase());
}

function renderAccountBadges(account: AgenticRobinhoodAccount): string {
  const badges: string[] = [];
  if (account.isDefault) badges.push("Default");
  if (account.agenticAllowed) badges.push("Agentic");
  if (!badges.length) return "";
  return badges
    .map((label) => `<span class="sfe-agentic-account-badge">${esc(label)}</span>`)
    .join("");
}

function renderAccountsList(accounts: AgenticRobinhoodAccount[]): string {
  if (!accounts.length) {
    return `<p class="muted small">No Robinhood accounts returned.</p>`;
  }
  const items = accounts
    .map((account) => {
      const subtitle = [
        account.tradingType ? formatTradingType(account.tradingType) : "",
        account.brokerageAccountType ? formatTradingType(account.brokerageAccountType) : "",
        account.maskedAccountNumber,
      ]
        .filter(Boolean)
        .join(" · ");
      return `
        <li class="sfe-agentic-account-item">
          <div class="sfe-agentic-account-main">
            <span class="sfe-agentic-account-name">${esc(account.name)}</span>
            ${renderAccountBadges(account)}
          </div>
          <span class="sfe-agentic-account-meta muted small">${esc(subtitle)}</span>
        </li>`;
    })
    .join("");
  return `<ul class="sfe-agentic-accounts">${items}</ul>`;
}

function formatMoney(value: unknown, currency = "USD"): string {
  const num =
    typeof value === "string" ? Number(value) : typeof value === "number" ? value : Number.NaN;
  if (!Number.isFinite(num)) return "—";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    maximumFractionDigits: 2,
  }).format(num);
}

function parseShareQuantity(value: unknown): number | null {
  const num =
    typeof value === "string" ? Number(value) : typeof value === "number" ? value : Number.NaN;
  if (!Number.isFinite(num)) return null;
  return Math.abs(num);
}

function formatShareQuantity(value: unknown): string {
  const num = parseShareQuantity(value);
  if (num == null) return "—";
  if (num > 0 && num < 1) return "<1";
  return String(Math.round(num));
}

function formatSignedShareDiff(diff: number): string {
  if (!Number.isFinite(diff)) return "—";
  if (Math.abs(diff) < 0.5) return "0";
  const rounded = Math.round(diff);
  return rounded > 0 ? `+${rounded}` : String(rounded);
}

function quantitiesMatch(rh: number | null, sf: number | null): boolean {
  if (rh == null && sf == null) return true;
  if (rh == null || sf == null) return false;
  return Math.abs(rh - sf) < 0.5;
}

function buildSfQtyMap(snapshot: PositionsSnapshot | null): SfQtyMap {
  const map: SfQtyMap = new Map();
  if (!snapshot) return map;
  for (const row of snapshot.stock) {
    const key = normalizeTicker(row.ticker);
    if (!key) continue;
    const qty = Number(row.quantity);
    if (!Number.isFinite(qty) || qty === 0) continue;
    map.set(key, (map.get(key) ?? 0) + Math.abs(qty));
  }
  return map;
}

/** Prefer own-ledger share inventory (same source as Stocks tab). */
async function loadReconcileShareQtyMap(force = false): Promise<SfQtyMap> {
  const own = await loadOwnLedgerShareAvgByTicker(force);
  if (own.size) {
    const map: SfQtyMap = new Map();
    for (const [ticker, row] of own) {
      const qty = Number(row.quantity);
      if (!Number.isFinite(qty) || qty === 0) continue;
      map.set(normalizeTicker(ticker), Math.abs(qty));
    }
    if (map.size) return map;
  }
  const snap = await loadPositionsSnapshot(force);
  return buildSfQtyMap(snap);
}

type SfOptionPos = {
  contracts: number;
  avgPrice: number | null;
  optionSymbol: string;
  ticker: string;
  side: string;
  strike: number;
  expDate: string;
};

type RhOptionPos = {
  ticker: string;
  side: string;
  strike: number;
  expDate: string;
  quantity: number;
  avgPrice: number | null;
  optionSymbol: string | null;
};

type RhEquityRow = {
  symbol: string;
  rhQty: number;
  avgCostHtml: string;
};

function readRecordNumber(obj: Record<string, unknown>, ...keys: string[]): number | null {
  for (const key of keys) {
    const value = obj[key];
    if (typeof value === "number" && Number.isFinite(value)) return value;
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) return parsed;
    }
  }
  return null;
}

function optionContractKey(ticker: string, side: string, strike: number, expDate: string): string {
  const sideNorm = side.toLowerCase().includes("call") ? "call" : "put";
  return `${normalizeTicker(ticker)}|${sideNorm}|${strike}|${expDate.slice(0, 10)}`;
}

function parseRhOptionSide(item: Record<string, unknown>): "call" | "put" | null {
  for (const key of ["option_type", "instrument_type"]) {
    const raw = item[key];
    if (typeof raw !== "string") continue;
    const lower = raw.toLowerCase();
    if (lower.includes("call")) return "call";
    if (lower.includes("put")) return "put";
  }
  const side = item.side;
  if (typeof side === "string") {
    const lower = side.toLowerCase();
    if (lower.includes("call")) return "call";
    if (lower.includes("put")) return "put";
  }
  const type = item.type;
  if (typeof type === "string") {
    const lower = type.toLowerCase();
    if (lower.includes("call")) return "call";
    if (lower.includes("put")) return "put";
  }
  return null;
}

function parseRhOptionRow(row: unknown): RhOptionPos | null {
  if (!row || typeof row !== "object" || Array.isArray(row)) return null;
  const item = row as Record<string, unknown>;
  const tickerRaw =
    (typeof item.chain_symbol === "string" && item.chain_symbol) ||
    (typeof item.symbol === "string" && item.symbol) ||
    (typeof item.underlying_symbol === "string" && item.underlying_symbol) ||
    "";
  const ticker = normalizeTicker(tickerRaw);
  const side = parseRhOptionSide(item);
  const strike = readRecordNumber(item, "strike_price", "strike");
  const expDateRaw = item.expiration_date ?? item.exp_date;
  const expDate = typeof expDateRaw === "string" ? expDateRaw.slice(0, 10) : "";
  const quantity = readRecordNumber(item, "quantity");
  if (!ticker || !side || strike == null || !expDate || quantity == null || !(Math.abs(quantity) > 0)) {
    return null;
  }
  const avgPriceRaw = readRecordNumber(
    item,
    "average_price",
    "avg_price",
    "premium",
    "average_open_price",
    "intraday_average_open_price",
  );
  const multiplier = readRecordNumber(item, "trade_value_multiplier");
  const avgPrice =
    avgPriceRaw != null ? normalizeRhOptionPremium(avgPriceRaw, multiplier ?? 100) : null;
  const optionSymbol =
    (typeof item.option_symbol === "string" && item.option_symbol) ||
    (typeof item.symbol === "string" && item.symbol.includes(ticker) ? item.symbol : null);
  return {
    ticker,
    side,
    strike,
    expDate,
    quantity: Math.abs(quantity),
    avgPrice,
    optionSymbol,
  };
}

function buildSfOptionMap(snapshot: PositionsSnapshot | null): Map<string, SfOptionPos> {
  const map = new Map<string, SfOptionPos>();
  if (!snapshot) return map;
  for (const row of snapshot.options) {
    if (row.expired) continue;
    const contracts = Number(row.contracts);
    if (!Number.isFinite(contracts) || Math.abs(contracts) === 0) continue;
    const side = row.side.toLowerCase().includes("call") ? "call" : "put";
    const key = optionContractKey(row.ticker, side, row.strike, row.exp_date);
    const qty = Math.abs(contracts);
    const existing = map.get(key);
    const avgPrice =
      row.entry_price ?? row.open_credit ?? existing?.avgPrice ?? null;
    map.set(key, {
      contracts: (existing?.contracts ?? 0) + qty,
      avgPrice,
      optionSymbol: row.option_symbol || existing?.optionSymbol || "",
      ticker: normalizeTicker(row.ticker),
      side,
      strike: row.strike,
      expDate: row.exp_date.slice(0, 10),
    });
  }
  return map;
}

function collectRhEquityRows(
  snapshot: AgenticSnapshot | null,
  accountNumber: string | null = null,
): Map<string, RhEquityRow> {
  const map = new Map<string, RhEquityRow>();
  if (!snapshot?.byAccount) return map;
  for (const [, entry] of snapshotAccountEntries(snapshot, accountNumber)) {
    const positionsRaw = readSnapshotData(entry, "equityPositions");
    if (!positionsRaw || typeof positionsRaw !== "object" || Array.isArray(positionsRaw)) continue;
    const positions = (positionsRaw as { positions?: unknown }).positions;
    if (!Array.isArray(positions)) continue;
    for (const row of positions) {
      if (!row || typeof row !== "object" || Array.isArray(row)) continue;
      const item = row as Record<string, unknown>;
      const symbol = typeof item.symbol === "string" ? item.symbol : "";
      const key = normalizeTicker(symbol);
      const rhQty = parseShareQuantity(item.quantity);
      if (!key || rhQty == null) continue;
      const avg =
        typeof item.average_buy_price === "string"
          ? formatMoney(item.average_buy_price)
          : "—";
      const existing = map.get(key);
      if (existing) {
        map.set(key, {
          symbol: key,
          rhQty: existing.rhQty + rhQty,
          avgCostHtml: existing.avgCostHtml !== "—" ? existing.avgCostHtml : avg !== "—" ? esc(avg) : "—",
        });
      } else {
        map.set(key, { symbol: key, rhQty, avgCostHtml: avg !== "—" ? esc(avg) : "—" });
      }
    }
  }
  return map;
}

function collectRhOptionMap(
  snapshot: AgenticSnapshot | null,
  accountNumber: string | null = null,
): Map<string, RhOptionPos> {
  const map = new Map<string, RhOptionPos>();
  if (!snapshot?.byAccount) return map;
  for (const [, entry] of snapshotAccountEntries(snapshot, accountNumber)) {
    const positionsRaw = readSnapshotData(entry, "optionPositions");
    if (!positionsRaw || typeof positionsRaw !== "object" || Array.isArray(positionsRaw)) continue;
    const positions = (positionsRaw as { positions?: unknown }).positions;
    if (!Array.isArray(positions)) continue;
    for (const row of positions) {
      const parsed = parseRhOptionRow(row);
      if (!parsed) continue;
      const key = optionContractKey(parsed.ticker, parsed.side, parsed.strike, parsed.expDate);
      const existing = map.get(key);
      if (!existing) {
        map.set(key, parsed);
        continue;
      }
      existing.quantity += parsed.quantity;
      if (existing.avgPrice == null && parsed.avgPrice != null) {
        existing.avgPrice = parsed.avgPrice;
      }
      if (!existing.optionSymbol && parsed.optionSymbol) {
        existing.optionSymbol = parsed.optionSymbol;
      }
    }
  }
  return map;
}

function formatContractQuantity(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  if (value > 0 && value < 1) return "<1";
  return String(Math.round(value));
}

function formatSignedContractDiff(diff: number): string {
  if (!Number.isFinite(diff)) return "—";
  if (Math.abs(diff) < 0.5) return "0";
  const rounded = Math.round(diff);
  return rounded > 0 ? `+${rounded}` : String(rounded);
}

function formatOptionContractLabel(pos: Pick<RhOptionPos, "ticker" | "side" | "strike" | "expDate">): string {
  const sideLabel = pos.side === "call" ? "Call" : "Put";
  return `${pos.ticker} ${sideLabel} · ${pos.strike} · ${pos.expDate}`;
}

function formatOptionPremium(value: number | null): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return formatMoney(value);
}

function collectRhEquitySymbols(
  snapshot: AgenticSnapshot | null,
  accountNumber: string | null = null,
): Set<string> {
  const symbols = new Set<string>();
  if (!snapshot?.byAccount) return symbols;
  for (const [, entry] of snapshotAccountEntries(snapshot, accountNumber)) {
    const positionsRaw = readSnapshotData(entry, "equityPositions");
    if (!positionsRaw || typeof positionsRaw !== "object" || Array.isArray(positionsRaw)) continue;
    const positions = (positionsRaw as { positions?: unknown }).positions;
    if (!Array.isArray(positions)) continue;
    for (const row of positions) {
      if (!row || typeof row !== "object" || Array.isArray(row)) continue;
      const symbol = (row as Record<string, unknown>).symbol;
      if (typeof symbol !== "string") continue;
      const key = normalizeTicker(symbol);
      if (key) symbols.add(key);
    }
  }
  return symbols;
}

function formatSnapshotTime(iso: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatSnapshotRefreshedAgo(iso: string | null, now = Date.now()): string {
  if (!iso) return "Not fully refreshed yet";
  const at = Date.parse(iso);
  if (!Number.isFinite(at)) return "Last refreshed —";
  const secs = Math.max(0, Math.floor((now - at) / 1000));
  if (secs < 60) {
    const n = secs <= 0 ? 1 : secs;
    return `Last refreshed ${n} sec ago`;
  }
  const mins = Math.floor(secs / 60);
  if (mins < 60) return `Last refreshed ${mins} min ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `Last refreshed ${hours} hr ago`;
  const days = Math.floor(hours / 24);
  return `Last refreshed ${days} day${days === 1 ? "" : "s"} ago`;
}

function snapshotRefreshedTitle(iso: string | null): string {
  if (!iso) return "";
  return `Last refreshed: ${formatSnapshotTime(iso)}`;
}

function readSnapshotData(entry: AgenticSnapshotAccountData | undefined, key: keyof AgenticSnapshotAccountData): unknown {
  const block = entry?.[key];
  if (!block || typeof block !== "object" || !("ok" in block) || !block.ok) return null;
  const data = block.data;
  if (!data || typeof data !== "object" || Array.isArray(data)) return null;
  return (data as { data?: unknown }).data ?? data;
}

const IMPORT_MARK_HEADS = `<th class="sfe-mark-head" title="Broker">Br</th><th class="sfe-mark-head sfe-acct-col" title="Account">Ac</th>`;

function importLedgerMarkCells(accountId: string): string {
  const mark = markForAccountId(accountId);
  return `${brokerMarkCellHtml(mark)}${accountShortCellHtml(mark)}`;
}

function renderEquityReconcileRow(opts: {
  symbol: string;
  rhQty: number | null;
  /** `undefined` = SF ledger not loaded; `null` = loaded but ticker absent. */
  sfQty: number | null | undefined;
  avgCostHtml: string;
  sfOnly?: boolean;
  accountId?: string;
}): string {
  const { symbol, rhQty, sfQty, avgCostHtml, sfOnly = false } = opts;
  const sfLoaded = sfQty !== undefined;
  const match = !sfLoaded || quantitiesMatch(rhQty, sfQty ?? null);
  const diff =
    !sfLoaded
      ? null
      : rhQty != null && sfQty != null
        ? sfQty - rhQty
        : rhQty == null && sfQty != null
          ? sfQty
          : rhQty != null && sfQty == null
            ? -rhQty
            : null;
  const rowClass = [
    sfLoaded && !match ? "sfe-agentic-equity-mismatch" : "",
    sfOnly ? "sfe-agentic-equity-sf-only" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const title =
    sfLoaded && !match
      ? sfOnly
        ? "In StockForge+ ledger but not in Robinhood"
        : sfQty == null
          ? "In Robinhood but missing from StockForge+ ledger"
          : "Share quantity mismatch vs StockForge+ ledger"
      : "";
  return `<tr class="${rowClass}"${title ? ` title="${esc(title)}"` : ""}>
      <td>${esc(symbol)}</td>
      ${importLedgerMarkCells(opts.accountId ?? "")}
      <td>${esc(rhQty == null ? "—" : formatShareQuantity(rhQty))}</td>
      <td>${esc(!sfLoaded || sfQty == null ? "—" : formatShareQuantity(sfQty))}</td>
      <td class="${sfLoaded && !match ? "sfe-agentic-equity-diff" : ""}">${esc(
        diff == null ? "—" : formatSignedShareDiff(diff),
      )}</td>
      <td>${avgCostHtml}</td>
    </tr>`;
}

function renderStockReconciliationSection(
  rhEquity: Map<string, RhEquityRow>,
  sfQtyByTicker: SfQtyMap | null,
  rhSymbols: Set<string>,
  accountId: string,
): string {
  if (!rhEquity.size && !sfQtyByTicker?.size) {
    return `<p class="muted small">No stock positions to compare.</p>`;
  }
  const sfHint =
    sfQtyByTicker == null ? `<p class="muted small">StockForge+ quantities unavailable.</p>` : "";
  const rows = [...rhEquity.values()]
    .filter((row) => scopeAllowsTicker(row.symbol))
    .sort((a, b) => a.symbol.localeCompare(b.symbol))
    .map((row) => {
      const sfQty: number | null | undefined = !sfQtyByTicker
        ? undefined
        : sfQtyByTicker.has(row.symbol)
          ? (sfQtyByTicker.get(row.symbol) ?? null)
          : null;
      return renderEquityReconcileRow({
        symbol: row.symbol,
        rhQty: row.rhQty,
        sfQty,
        avgCostHtml: row.avgCostHtml,
        accountId,
      });
    })
    .join("");
  const table = rhEquity.size
    ? `${sfHint}
    <table class="sfe-agentic-snapshot-table sfe-agentic-equity-reconcile">
      <thead><tr><th>Symbol</th>${IMPORT_MARK_HEADS}<th>Robinhood</th><th>StockForge+</th><th>Diff</th><th>Avg cost</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`
    : `<p class="muted small">No Robinhood equity positions in snapshot.</p>`;
  return `${table}${renderSfOnlyEquitySection(sfQtyByTicker, rhSymbols, accountId)}`;
}

function renderOptionReconcileRow(opts: {
  label: string;
  rhQty: number | null;
  sfQty: number | null | undefined;
  avgPremiumHtml: string;
  sfOnly?: boolean;
  accountId?: string;
}): string {
  const { label, rhQty, sfQty, avgPremiumHtml, sfOnly = false } = opts;
  const sfLoaded = sfQty !== undefined;
  const match = !sfLoaded || quantitiesMatch(rhQty, sfQty ?? null);
  const diff =
    !sfLoaded
      ? null
      : rhQty != null && sfQty != null
        ? sfQty - rhQty
        : rhQty == null && sfQty != null
          ? sfQty
          : rhQty != null && sfQty == null
            ? -rhQty
            : null;
  const rowClass = [
    sfLoaded && !match ? "sfe-agentic-equity-mismatch" : "",
    sfOnly ? "sfe-agentic-equity-sf-only" : "",
  ]
    .filter(Boolean)
    .join(" ");
  const title =
    sfLoaded && !match
      ? sfOnly
        ? "In StockForge+ ledger but not in Robinhood"
        : sfQty == null
          ? "In Robinhood but missing from StockForge+ ledger"
          : "Contract quantity mismatch vs StockForge+ ledger"
      : "";
  return `<tr class="${rowClass}"${title ? ` title="${esc(title)}"` : ""}>
      <td>${esc(label)}</td>
      ${importLedgerMarkCells(opts.accountId ?? "")}
      <td>${esc(rhQty == null ? "—" : formatContractQuantity(rhQty))}</td>
      <td>${esc(!sfLoaded || sfQty == null ? "—" : formatContractQuantity(sfQty))}</td>
      <td class="${sfLoaded && !match ? "sfe-agentic-equity-diff" : ""}">${esc(
        diff == null ? "—" : formatSignedContractDiff(diff),
      )}</td>
      <td>${avgPremiumHtml}</td>
    </tr>`;
}

function renderSfOnlyOptionsSection(
  sfOptionByContract: Map<string, SfOptionPos> | null,
  rhKeys: Set<string>,
  accountId: string,
): string {
  if (!sfOptionByContract) return "";
  const extras = [...sfOptionByContract.entries()]
    .filter(([key, pos]) => pos.contracts > 0 && !rhKeys.has(key))
    .sort(([a], [b]) => a.localeCompare(b));
  if (!extras.length) return "";
  const rows = extras
    .map(([, pos]) =>
      renderOptionReconcileRow({
        label: formatOptionContractLabel(pos),
        rhQty: null,
        sfQty: pos.contracts,
        avgPremiumHtml: esc(formatOptionPremium(pos.avgPrice)),
        sfOnly: true,
        accountId,
      }),
    )
    .join("");
  return `
    <div class="sfe-agentic-sf-only-equity">
      <h4 class="sfe-agentic-subsection-title">In StockForge+ only</h4>
      <p class="muted small">Open in the StockForge+ ledger but not present in any Robinhood option snapshot.</p>
      <table class="sfe-agentic-snapshot-table sfe-agentic-equity-reconcile">
        <thead><tr><th>Contract</th>${IMPORT_MARK_HEADS}<th>Robinhood</th><th>StockForge+</th><th>Diff</th><th>Avg premium</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

function renderOptionsReconciliationSection(
  rhOptions: Map<string, RhOptionPos>,
  sfOptionByContract: Map<string, SfOptionPos> | null,
  accountId: string,
): string {
  if (!rhOptions.size && !sfOptionByContract?.size) {
    return `<p class="muted small">No option positions to compare.</p>`;
  }
  const sfHint =
    sfOptionByContract == null
      ? `<p class="muted small">StockForge+ option quantities unavailable.</p>`
      : "";
  const rows = [...rhOptions.entries()]
    .filter(([, rh]) => scopeAllowsTicker(rh.ticker))
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, rh]) => {
      const sf = sfOptionByContract?.get(key);
      const sfQty: number | null | undefined = !sfOptionByContract
        ? undefined
        : sf
          ? sf.contracts
          : null;
      const avgPremium =
        rh.avgPrice != null
          ? esc(formatOptionPremium(rh.avgPrice))
          : sf?.avgPrice != null
            ? esc(formatOptionPremium(sf.avgPrice))
            : "—";
      return renderOptionReconcileRow({
        label: formatOptionContractLabel(rh),
        rhQty: rh.quantity,
        sfQty,
        avgPremiumHtml: avgPremium,
        accountId,
      });
    })
    .join("");
  const table = rhOptions.size
    ? `${sfHint}
    <table class="sfe-agentic-snapshot-table sfe-agentic-equity-reconcile">
      <thead><tr><th>Contract</th>${IMPORT_MARK_HEADS}<th>Robinhood</th><th>StockForge+</th><th>Diff</th><th>Avg premium</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>`
    : `<p class="muted small">No Robinhood option positions in snapshot.</p>`;
  return `${table}${renderSfOnlyOptionsSection(sfOptionByContract, new Set(rhOptions.keys()), accountId)}`;
}

function renderReconciliationPanel(
  snapshot: AgenticSnapshot | null,
  sfQtyByTicker: SfQtyMap | null,
  sfOptionByContract: Map<string, SfOptionPos> | null,
  sectionOpen: boolean,
  selectedAccountNumber: string | null,
  ledgerAccountId: string,
): string {
  if (!snapshot && sfQtyByTicker == null && sfOptionByContract == null) return "";

  const rhEquity = collectRhEquityRows(snapshot, selectedAccountNumber);
  const rhOptions = collectRhOptionMap(snapshot, selectedAccountNumber);

  return `
    <details class="collapsible sfe-agentic-reconcile-collapsible" data-agentic-reconcile-section data-sfe-collapsible="reconcile"${sectionOpen ? " open" : ""}>
      <summary class="sfe-agentic-reconcile-summary">
        <h2 class="insight-h">Reconciliation</h2>
      </summary>
      <div class="sfe-agentic-reconcile-panel">
        <p class="muted small">Diff = StockForge+ − Robinhood. Compares Robinhood snapshot holdings to the StockForge+ ledger.</p>
        <h4 class="sfe-agentic-subsection-title">Stock positions</h4>
        ${renderStockReconciliationSection(
          rhEquity,
          sfQtyByTicker,
          collectRhEquitySymbols(snapshot, selectedAccountNumber),
          ledgerAccountId,
        )}
        <h4 class="sfe-agentic-subsection-title">Options</h4>
        ${renderOptionsReconciliationSection(rhOptions, sfOptionByContract, ledgerAccountId)}
      </div>
    </details>`;
}

function renderSfOnlyEquitySection(
  sfQtyByTicker: SfQtyMap | null,
  rhSymbols: Set<string>,
  accountId: string,
): string {
  if (!sfQtyByTicker) return "";
  const extras = [...sfQtyByTicker.entries()]
    .filter(([ticker, qty]) => qty > 0 && !rhSymbols.has(ticker))
    .sort(([a], [b]) => a.localeCompare(b));
  if (!extras.length) return "";
  const rows = extras
    .map(([ticker, qty]) =>
      renderEquityReconcileRow({
        symbol: ticker,
        rhQty: null,
        sfQty: qty,
        avgCostHtml: "—",
        sfOnly: true,
        accountId,
      }),
    )
    .join("");
  return `
    <div class="sfe-agentic-sf-only-equity">
      <h4 class="sfe-agentic-subsection-title">In StockForge+ only</h4>
      <p class="muted small">Held in the StockForge+ ledger but not present in any Robinhood equity snapshot.</p>
      <table class="sfe-agentic-snapshot-table sfe-agentic-equity-reconcile">
        <thead><tr><th>Symbol</th>${IMPORT_MARK_HEADS}<th>Robinhood</th><th>StockForge+</th><th>Diff</th><th>Avg cost</th></tr></thead>
        <tbody>${rows}</tbody>
      </table>
    </div>`;
}

function renderSnapshotAccount(
  account: AgenticRobinhoodAccount,
  entry: AgenticSnapshotAccountData | undefined,
): string {
  const portfolioRaw = readSnapshotData(entry, "portfolio");
  const portfolio =
    portfolioRaw && typeof portfolioRaw === "object" && !Array.isArray(portfolioRaw)
      ? (portfolioRaw as Record<string, unknown>)
      : null;
  const buyingPower =
    portfolio?.buying_power &&
    typeof portfolio.buying_power === "object" &&
    !Array.isArray(portfolio.buying_power)
      ? (portfolio.buying_power as Record<string, unknown>)
      : null;
  const currency =
    typeof buyingPower?.display_currency === "string"
      ? buyingPower.display_currency
      : typeof portfolio?.currency === "string"
        ? portfolio.currency
        : "USD";

  const subtitle = [
    account.tradingType ? formatTradingType(account.tradingType) : "",
    account.maskedAccountNumber,
  ]
    .filter(Boolean)
    .join(" · ");

  const margin = deriveRobinhoodMarginSummary(portfolio, {
    isMarginAccount: account.tradingType?.toLowerCase() === "margin",
  });
  const cashRaw = portfolio?.cash;
  const cashNum =
    typeof cashRaw === "number"
      ? cashRaw
      : typeof cashRaw === "string"
        ? Number(cashRaw)
        : NaN;
  const cashCls = Number.isFinite(cashNum) && cashNum < 0 ? " sfe-neg" : "";
  const marginMetrics = margin
    ? `
      <h4 class="sfe-agentic-subsection-title">Margin</h4>
      <div class="kpi-row sfe-agentic-kpi-row sfe-agentic-margin-kpi-row">
        <div class="kpi">
          <span class="kpi-k">Margin used</span>
          <span class="kpi-v">${esc(formatMoney(margin.marginUsed, currency))}</span>
        </div>
        <div class="kpi">
          <span class="kpi-k">Margin available</span>
          <span class="kpi-v">${esc(formatMoney(margin.marginAvailable, currency))}</span>
        </div>
        <div class="kpi">
          <span class="kpi-k">Margin buffer</span>
          <span class="kpi-v">${esc(formatMarginPercent(margin.marginBufferPct))}</span>
        </div>
        <div class="kpi">
          <span class="kpi-k">Annual rate</span>
          <span class="kpi-v">${esc(formatMarginRate(margin.annualRatePct))}</span>
        </div>
        <div class="kpi">
          <span class="kpi-k">Daily interest</span>
          <span class="kpi-v">${esc(formatMoney(margin.dailyInterest, currency))}</span>
        </div>
      </div>`
    : "";

  return `
    <article class="sfe-agentic-snapshot-account">
      <div class="sfe-agentic-account-main">
        <span class="sfe-agentic-account-name">${esc(account.name)}</span>
        ${renderAccountBadges(account)}
      </div>
      <span class="sfe-agentic-account-meta muted small">${esc(subtitle)}</span>
      <div class="kpi-row sfe-agentic-kpi-row">
        <div class="kpi">
          <span class="kpi-k">Portfolio value</span>
          <span class="kpi-v">${esc(formatMoney(portfolio?.total_value, currency))}</span>
        </div>
        <div class="kpi">
          <span class="kpi-k">Buying power</span>
          <span class="kpi-v">${esc(formatMoney(buyingPower?.buying_power, currency))}</span>
        </div>
        <div class="kpi">
          <span class="kpi-k">Cash</span>
          <span class="kpi-v${cashCls}">${esc(formatMoney(portfolio?.cash, currency))}</span>
        </div>
      </div>
      ${marginMetrics}
    </article>`;
}

function renderSnapshotPanel(
  snapshot: AgenticSnapshot | null,
  connected: boolean,
  refreshing: boolean,
  sectionOpen: boolean,
  selectedAccountNumber: string | null,
): string {
  if (!snapshot && !connected) return "";

  const refreshedAt = snapshot?.refreshedAt ?? snapshot?.capturedAt ?? null;
  const summaryHint = refreshedAt
    ? formatSnapshotRefreshedAgo(refreshedAt)
    : connected
      ? "No snapshot saved yet"
      : "Saved on proxy";
  const summaryTitle = snapshotRefreshedTitle(refreshedAt);

  const body = !snapshot
    ? `
          <p class="muted small">Robinhood responses are saved automatically. Configure sync interval in the Import section above.</p>`
    : (() => {
        const accounts = (snapshot.accounts ?? []).filter(
          (account) =>
            !selectedAccountNumber || account.accountNumber === selectedAccountNumber,
        );
        const accountBlocks = accounts
          .map((account) =>
            renderSnapshotAccount(account, snapshot.byAccount?.[account.accountNumber]),
          )
          .join("");
        const refreshedLabel = formatSnapshotRefreshedAgo(refreshedAt);
        const refreshedTitle = snapshotRefreshedTitle(refreshedAt);
        const autoSavedLabel =
          snapshot.updatedAt &&
          refreshedAt &&
          Date.parse(snapshot.updatedAt) > Date.parse(refreshedAt)
            ? `<p class="muted small sfe-agentic-snapshot-autosave">Auto-saved ${esc(formatSnapshotTime(snapshot.updatedAt))}</p>`
            : snapshot.updatedAt && !refreshedAt
              ? `<p class="muted small sfe-agentic-snapshot-autosave">Auto-saved ${esc(formatSnapshotTime(snapshot.updatedAt))}</p>`
              : "";
        return `
          <div class="sfe-agentic-snapshot-header">
            <div>
              <p class="muted small sfe-agentic-snapshot-time" data-agentic-snapshot-refreshed-time data-refreshed-at="${refreshedAt ? esc(refreshedAt) : ""}" title="${esc(refreshedTitle)}">${esc(refreshedLabel)}</p>
              ${autoSavedLabel}
            </div>
          </div>
          <p class="muted small">Stored encrypted on the proxy.</p>
          <div class="sfe-agentic-snapshot-accounts">${accountBlocks}</div>`;
      })();

  return `
    <details class="collapsible sfe-agentic-snapshot-collapsible" data-agentic-snapshot-section data-sfe-collapsible="snapshot"${sectionOpen ? " open" : ""}>
      <summary class="sfe-agentic-snapshot-summary">
        <h2 class="insight-h">Snapshot</h2>
        <span class="muted small sfe-agentic-snapshot-summary-hint" data-agentic-snapshot-refreshed-summary data-refreshed-at="${refreshedAt ? esc(refreshedAt) : ""}" title="${esc(summaryTitle)}">${esc(summaryHint)}</span>
      </summary>
      <div class="sfe-agentic-snapshot-panel">${body}</div>
    </details>`;
}

export class ImportController {
  private host: HTMLElement | null = null;
  private tools: AgenticToolDefinition[] = [];
  private selectedTool = "";
  private loading = false;
  private error = "";
  private connected = false;
  private resultText = "";
  private returnPath = "/web/";
  private pendingAuthUrl: string | null = readPendingAuthUrl();
  private oauthCooldown: AgenticOAuthCooldown | null = null;
  private cooldownTimer: ReturnType<typeof setInterval> | null = null;
  private accounts: AgenticRobinhoodAccount[] = [];
  private selectedAccountNumber: string | null = null;
  private snapshot: AgenticSnapshot | null = null;
  private sfQtyByTicker: SfQtyMap | null = null;
  private sfOptionByContract: Map<string, SfOptionPos> | null = null;
  private snapshotRefreshing = false;
  private tokenExpiry: AgenticTokenExpiry | null = null;
  private tokenExpiryTimer: ReturnType<typeof setInterval> | null = null;
  private agenticSettings: AgenticSettings = readCachedAgenticSettings(DEFAULT_AGENTIC_SETTINGS);
  /** False until the server has answered once, so sync timers stay hidden. */
  private syncStatusLoaded = false;
  private nextAutoRefreshSec: number | null = null;
  private autoRefreshTimer: ReturnType<typeof setInterval> | null = null;
  private settingsChangedHandler: (() => void) | null = null;
  private snapshotSectionOpen = isCollapsibleOpen(IMPORT_COLLAPSIBLE_SCOPE, "snapshot", true);
  private reconcileSectionOpen = isCollapsibleOpen(IMPORT_COLLAPSIBLE_SCOPE, "reconcile", true);
  private updatesSectionOpen = isCollapsibleOpen(IMPORT_COLLAPSIBLE_SCOPE, "updates", true);
  private updates: AgenticUpdateItem[] = [];
  private hasPreviousSnapshot = false;
  private updatesPendingCount = 0;
  private updatesHistoryCount = 0;
  private updatesLoadError: string | null = null;
  private expandedUpdateIds = new Set<string>();
  private ledgerHydratingIds = new Set<string>();
  private addFormOpenId: string | null = null;
  private busyUpdateId: string | null = null;
  private ledgerByUpdateId = new Map<string, WheelLedgerTransaction[]>();
  private snapshotDuePollAt = 0;
  private importSettingsCtx: ImportSettingsContext = {
    settings: this.agenticSettings,
    snapshotRefreshedAt: null,
    nextAutoRefreshSec: null,
    loading: true,
    catalog: emptyLedgerCatalog(),
    selectedBrokerId: "",
    selectedAccountId: "",
  };
  private ledgerCatalog: LedgerCatalogState = emptyLedgerCatalog();
  private selectedImportBrokerId = "";
  private selectedImportAccountId = "";
  private importSettingsCleanup: (() => void) | null = null;
  private marksCleanup: (() => void) | null = null;
  private scopeCleanup: (() => void) | null = null;
  private hostClickHandler: ((event: Event) => void) | null = null;

  mount(panel: HTMLElement, opts: { returnPath?: string } = {}): void {
    this.host = panel;
    this.returnPath = opts.returnPath ?? "/web/";
    this.hostClickHandler = (event) => this.handleHostClick(event);
    this.host.addEventListener("click", this.hostClickHandler);
    this.marksCleanup?.();
    this.marksCleanup = subscribeLedgerMarks(() => {
      if (this.host) this.render();
    });
    this.scopeCleanup?.();
    this.scopeCleanup = subscribeAccountScope(() => {
      if (this.host?.isConnected) this.render();
    });
    void ensureLedgerMarkIndex().catch(() => null);
    this.settingsChangedHandler = () => {
      void this.loadSnapshot();
    };
    window.addEventListener(AGENTIC_SETTINGS_CHANGED_EVENT, this.settingsChangedHandler);
    this.render();
    void this.refresh();
  }

  unmount(): void {
    if (this.settingsChangedHandler) {
      window.removeEventListener(AGENTIC_SETTINGS_CHANGED_EVENT, this.settingsChangedHandler);
      this.settingsChangedHandler = null;
    }
    this.clearCooldownTimer();
    this.clearTokenExpiryTimer();
    this.clearAutoRefreshTimer();
    this.importSettingsCleanup?.();
    this.importSettingsCleanup = null;
    this.marksCleanup?.();
    this.marksCleanup = null;
    this.scopeCleanup?.();
    this.scopeCleanup = null;
    if (this.host && this.hostClickHandler) {
      this.host.removeEventListener("click", this.hostClickHandler);
      this.hostClickHandler = null;
    }
    this.host = null;
  }

  private clearAutoRefreshTimer(): void {
    if (this.autoRefreshTimer) {
      clearInterval(this.autoRefreshTimer);
      this.autoRefreshTimer = null;
    }
  }

  /** Server stays the source of truth; the mirror only seeds the next first paint. */
  private applyAgenticSettings(next: Partial<AgenticSettings> | null | undefined): void {
    if (!next) return;
    this.agenticSettings = { ...this.agenticSettings, ...next };
    this.syncStatusLoaded = true;
    cacheAgenticSettings(this.agenticSettings);
  }

  private syncAutoRefreshTimer(): void {
    this.clearAutoRefreshTimer();
    if (!this.agenticSettings.snapshotAutoRefreshEnabled) return;
    this.autoRefreshTimer = setInterval(() => {
      if (this.nextAutoRefreshSec === null) return;
      if (this.nextAutoRefreshSec <= 0) {
        const now = Date.now();
        if (now - this.snapshotDuePollAt < 15_000) {
          this.updateLiveStatusTexts();
          return;
        }
        this.snapshotDuePollAt = now;
        void this.loadSnapshot();
        return;
      }
      this.nextAutoRefreshSec -= 1;
      this.importSettingsCtx.nextAutoRefreshSec = this.nextAutoRefreshSec;
      this.updateLiveStatusTexts();
    }, 1000);
  }

  private tokenExpiryStatusText(): string {
    if (!this.connected || !this.tokenExpiry?.accessTokenExpiresAt) return "";
    const sec = this.tokenExpiry.accessTokenExpiresInSec ?? 0;
    const when =
      sec > 0
        ? `expires in ${formatExpiresIn(sec)}`
        : "expired — will refresh on next Robinhood call";
    const refreshNote = this.tokenExpiry.hasRefreshToken ? " Auto-refreshes when you use Agentic." : "";
    const at = formatSnapshotTime(this.tokenExpiry.accessTokenExpiresAt);
    return `Access token ${when} (${at}).${refreshNote}`;
  }

  /** Update token-expiry / last-refreshed text without rebuilding the snapshot tables. */
  private updateLiveStatusTexts(): void {
    if (!this.host) return;
    const tokenEl = this.host.querySelector<HTMLElement>("[data-agentic-token-expiry]");
    if (tokenEl) {
      const text = this.tokenExpiryStatusText();
      if (text) tokenEl.textContent = text;
    }
    for (const el of this.host.querySelectorAll<HTMLElement>(
      "[data-agentic-snapshot-refreshed-time], [data-agentic-snapshot-refreshed-summary]",
    )) {
      const iso = el.dataset.refreshedAt;
      if (!iso) continue;
      el.textContent = formatSnapshotRefreshedAgo(iso);
      el.title = snapshotRefreshedTitle(iso);
    }
    this.importSettingsCtx.nextAutoRefreshSec = this.nextAutoRefreshSec;
    this.importSettingsCtx.snapshotRefreshedAt = this.snapshotRefreshedAt();
    this.importSettingsCtx.settings = this.agenticSettings;
    this.importSettingsCtx.loading = !this.syncStatusLoaded;
    const importMetaEl = this.host.querySelector<HTMLElement>("[data-agentic-import-meta]");
    if (importMetaEl) {
      importMetaEl.textContent = formatImportMetaText(this.importSettingsCtx);
    }
  }

  private clearTokenExpiryTimer(): void {
    if (this.tokenExpiryTimer) {
      clearInterval(this.tokenExpiryTimer);
      this.tokenExpiryTimer = null;
    }
  }

  private syncTokenExpiryTimer(): void {
    this.clearTokenExpiryTimer();
    if (!this.connected || !this.tokenExpiry?.accessTokenExpiresAt) return;
    this.tokenExpiryTimer = setInterval(() => {
      if (!this.tokenExpiry?.accessTokenExpiresAt) {
        this.clearTokenExpiryTimer();
        return;
      }
      const expiresAtMs = Date.parse(this.tokenExpiry.accessTokenExpiresAt);
      if (!Number.isFinite(expiresAtMs)) {
        this.clearTokenExpiryTimer();
        return;
      }
      const sec = Math.max(0, Math.ceil((expiresAtMs - Date.now()) / 1000));
      this.tokenExpiry = { ...this.tokenExpiry, accessTokenExpiresInSec: sec };
      this.updateLiveStatusTexts();
    }, 60_000);
  }

  private clearCooldownTimer(): void {
    if (this.cooldownTimer) {
      clearInterval(this.cooldownTimer);
      this.cooldownTimer = null;
    }
  }

  private syncCooldownTimer(): void {
    this.clearCooldownTimer();
    if (!this.oauthCooldown?.startBlocked || this.oauthCooldown.retryAfterSec <= 0) return;
    this.cooldownTimer = setInterval(() => {
      if (!this.oauthCooldown?.startBlocked) {
        this.clearCooldownTimer();
        return;
      }
      if (this.oauthCooldown.retryAfterSec <= 1) {
        this.oauthCooldown = {
          ...this.oauthCooldown,
          blocked: false,
          startBlocked: false,
          canStart: true,
          retryAfterSec: 0,
          message: null,
        };
        this.clearCooldownTimer();
        this.render();
        return;
      }
      this.oauthCooldown = {
        ...this.oauthCooldown,
        retryAfterSec: this.oauthCooldown.retryAfterSec - 1,
        message: `Robinhood OAuth is cooling down. Try again in ${formatRetryAfter(this.oauthCooldown.retryAfterSec - 1)}.`,
      };
      this.render();
    }, 1000);
  }

  async refresh(force = false): Promise<void> {
    if (!this.host) return;
    if (this.loading && !force) return;
    this.loading = true;
    this.error = "";
    this.render();
    try {
      const [status, snapshotResponse] = await Promise.all([
        fetchAgenticStatus(),
        fetchAgenticSnapshot(),
        this.loadLedgerImportTarget(),
      ]);
      this.connected = status.connected;
      this.oauthCooldown = status.oauthCooldown ?? null;
      this.tokenExpiry = status.tokenExpiry ?? null;
      this.snapshot = snapshotResponse.snapshot;
      this.applyAgenticSettings(snapshotResponse.settings);
      this.nextAutoRefreshSec = snapshotResponse.nextAutoRefreshSec ?? null;
      this.syncCooldownTimer();
      this.syncTokenExpiryTimer();
      this.syncAutoRefreshTimer();
      if (this.connected) {
        this.setPendingAuthUrl(null);
        const [tools, accounts] = await Promise.all([fetchAgenticTools(), fetchAgenticAccounts()]);
        this.tools = tools;
        this.accounts = accounts;
        await Promise.all([this.loadSnapshot(), this.loadSfPositions(), this.loadUpdates(true)]);
        if (!this.selectedTool && this.tools[0]?.name) {
          this.selectedTool = this.tools[0].name;
        }
      } else {
        this.tools = [];
        this.accounts = this.snapshot?.accounts ?? [];
        await Promise.all([this.loadSfPositions(), this.loadUpdates(true)]);
      }
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to load Import status";
    } finally {
      this.loading = false;
      // A failed load still ends the wait; the panel shows the error instead.
      this.syncStatusLoaded = true;
      this.render();
      notifyRobinhoodStatusChanged();
    }
  }

  private async loadLedgerImportTarget(): Promise<void> {
    try {
      const status = await loadOwnLedgerImportStatus(true);
      this.ledgerCatalog = {
        brokers: status?.brokers ?? [],
        accounts: status?.accounts ?? [],
      };
      const selection = resolveImportTargetSelection(
        this.ledgerCatalog,
        status?.settings.defaultRobinhoodBrokerId,
        status?.settings.defaultRobinhoodAccountId,
      );
      this.selectedImportBrokerId = selection.brokerId;
      this.selectedImportAccountId = selection.accountId;
    } catch {
      this.ledgerCatalog = emptyLedgerCatalog();
    }
  }

  private async loadSnapshot(): Promise<void> {
    const snapshotResponse = await fetchAgenticSnapshot();
    this.snapshot = snapshotResponse.snapshot;
    this.applyAgenticSettings(snapshotResponse.settings);
    this.nextAutoRefreshSec = snapshotResponse.nextAutoRefreshSec ?? null;
    if (!this.connected && this.snapshot?.accounts?.length) {
      this.accounts = this.snapshot.accounts;
    }
    await Promise.all([this.loadUpdates(), this.loadSfPositions()]);
    this.syncAutoRefreshTimer();
    this.render();
  }

  private async loadSfPositions(force = false): Promise<void> {
    try {
      const [shareQty, ledgerOpts] = await Promise.all([
        loadReconcileShareQtyMap(force),
        loadOwnLedgerOpenOptions(force).catch(() => []),
      ]);
      this.sfQtyByTicker = shareQty;
      if (ledgerOpts.length) {
        const pseudo: PositionsSnapshot = {
          options: ledgerOpts.map((row) => ({
            option_symbol: row.optionSymbol,
            ticker: row.ticker,
            side: row.side,
            strike: row.strike,
            exp_date: row.expDate,
            contracts: row.contracts,
            entry_price: row.entryPrice,
            open_credit: row.openCredit,
            expired: false,
          })),
          stock: [],
          fetchedAt: Date.now(),
        };
        this.sfOptionByContract = buildSfOptionMap(pseudo);
      } else {
        const snap = await loadPositionsSnapshot(force);
        this.sfOptionByContract = buildSfOptionMap(snap);
      }
    } catch {
      this.sfQtyByTicker = null;
      this.sfOptionByContract = null;
    }
  }

  private async loadUpdates(forceLedger = false): Promise<void> {
    try {
      const response = await fetchAgenticUpdates();
      this.updates = response.updates ?? [];
      this.hasPreviousSnapshot = Boolean(response.hasPreviousSnapshot);
      this.updatesPendingCount = response.pendingCount ?? this.updates.filter((u) => u.status === "pending").length;
      this.updatesHistoryCount = response.historyCount ?? this.updates.length;
      this.applyAgenticSettings(response.settings);
      this.updatesLoadError = null;
      const autoImported = (response.autoImport?.imported ?? 0) + (response.autoImport?.linked ?? 0);
      if (autoImported > 0) {
        invalidateTransactionsSnapshot();
        await this.loadSfPositions(true);
        await afterWheelLedgerChange().catch(() => {});
      }
      await this.hydrateLedgerDetails();
    } catch (err) {
      this.updatesLoadError =
        err instanceof Error && err.message
          ? err.message
          : "Failed to load Import history.";
    }
  }

  private snapshotRefreshedAt(): string | null {
    return this.snapshot?.refreshedAt ?? this.snapshot?.updatedAt ?? null;
  }

  private async hydrateLedgerDetails(): Promise<void> {
    const targets = this.updates.filter((update) => this.expandedUpdateIds.has(update.id));
    if (!targets.length) return;

    const all = await fetchAllLedgerTransactions();
    const byId = new Map(all.map((txn) => [txn.id, txn]));
    const next = new Map(this.ledgerByUpdateId);

    for (const update of targets) {
      const input = agenticUpdateToLedgerInput(update);
      let ids = update.ledgerTxnIds.filter((id) => Number.isFinite(id));
      if (!ids.length) {
        const tradeDate = update.handledAt?.slice(0, 10) || undefined;
        ids = findLedgerTxnIdsInTransactions(input, all, tradeDate);
        if (!ids.length) {
          ids = findLedgerTxnIdsInTransactions(input, all);
        }
      }
      const txns = ids
        .map((id) => byId.get(id))
        .filter((txn): txn is WheelLedgerTransaction => Boolean(txn));
      next.set(update.id, txns);
    }

    this.ledgerByUpdateId = next;
  }

  private toggleUpdateExpanded(updateId: string): void {
    const expanding = !this.expandedUpdateIds.has(updateId);
    if (expanding) {
      this.expandedUpdateIds.add(updateId);
      this.ledgerHydratingIds.add(updateId);
    } else {
      this.expandedUpdateIds.delete(updateId);
      this.ledgerHydratingIds.delete(updateId);
    }
    this.render();
    if (!expanding) return;
    void this.hydrateLedgerDetails()
      .catch(() => {
        /* ledger details are optional */
      })
      .finally(() => {
        this.ledgerHydratingIds.delete(updateId);
        if (this.expandedUpdateIds.has(updateId)) {
          this.render();
        }
      });
  }

  private handleHostClick(event: Event): void {
    const button = (event.target as HTMLElement).closest<HTMLElement>("[data-agentic-update-action]");
    if (!button || !this.host?.contains(button)) return;
    const action = button.dataset.agenticUpdateAction;
    const updateId = button.dataset.agenticUpdateId;
    if (!action || !updateId) return;
    if (action === "toggle") {
      event.preventDefault();
      event.stopPropagation();
      this.toggleUpdateExpanded(updateId);
      return;
    }
    if (action === "add") {
      void this.handleUpdateAdd(updateId);
      return;
    }
    if (action === "manual") {
      void this.handleUpdateManual(updateId);
    }
  }

  private defaultAvgPriceForUpdate(update: AgenticUpdateItem): string {
    if (update.avgPrice != null && Number.isFinite(update.avgPrice)) {
      const normalized =
        update.avgPrice < 0
          ? normalizeRhOptionPremium(update.avgPrice)
          : update.avgPrice;
      return Number(normalized).toFixed(2);
    }
    if (update.kind === "asgn") return String(update.strike);
    return update.kind === "exp" ? "0" : "";
  }

  private findUpdate(updateId: string): AgenticUpdateItem | undefined {
    return this.updates.find((item) => item.id === updateId);
  }

  private async refreshAfterLedgerWrite(): Promise<void> {
    try {
      await Promise.all([this.loadSfPositions(true), this.loadUpdates(true)]);
    } catch {
      /* SF+ / updates reload is best-effort after the ledger write. */
    }
    this.render();
    await afterWheelLedgerChange();
  }

  private async markUpdateHandled(
    update: AgenticUpdateItem,
    status: "added" | "manual",
    ledgerTxnIds: number[],
  ): Promise<void> {
    await patchAgenticUpdate(update.id, {
      status,
      ledgerTxnIds,
      update,
    });
    update.status = status;
    update.ledgerTxnIds = ledgerTxnIds;
    update.handledAt = new Date().toISOString();
    this.updatesPendingCount = this.updates.filter((item) => item.status === "pending").length;
    invalidateTransactionsSnapshot();
    this.expandedUpdateIds.add(update.id);
    this.addFormOpenId = null;
    await this.refreshAfterLedgerWrite();
  }

  private async handleUpdateManual(updateId: string): Promise<void> {
    const update = this.findUpdate(updateId);
    if (!update || update.status !== "pending") return;
    this.busyUpdateId = updateId;
    this.error = "";
    this.render();
    try {
      let ledgerTxnIds: number[] = [];
      try {
        ledgerTxnIds = await findLedgerTxnIdsForAgenticUpdate(agenticUpdateToLedgerInput(update));
      } catch {
        ledgerTxnIds = [];
      }
      await this.markUpdateHandled(update, "manual", ledgerTxnIds);
      showToast(
        ledgerTxnIds.length
          ? "Marked manual — linked existing ledger transaction."
          : "Marked manual.",
        "success",
      );
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to mark update manual";
      showToast(this.error, "error");
    } finally {
      this.busyUpdateId = null;
      this.render();
    }
  }

  private async handleUpdateAdd(updateId: string): Promise<void> {
    const update = this.findUpdate(updateId);
    if (!update || update.status !== "pending") return;
    if (this.addFormOpenId !== updateId) {
      this.addFormOpenId = updateId;
      this.render();
      return;
    }
    const form = this.host?.querySelector<HTMLFormElement>(`[data-agentic-update-form="${updateId}"]`);
    const tradeDate = form?.querySelector<HTMLInputElement>("[data-agentic-update-trade-date]")?.value?.trim();
    const avgRaw = form?.querySelector<HTMLInputElement>("[data-agentic-update-avg-price]")?.value?.trim();
    const avgPrice = avgRaw ? Number(avgRaw) : undefined;
    if (!tradeDate) {
      this.error = "Choose a trade date.";
      this.render();
      return;
    }
    this.busyUpdateId = updateId;
    this.error = "";
    this.render();
    let reservedAdded = false;
    let ledgerWritten = false;
    try {
      // Reserve "added" before writing the ledger so auto-refresh won't mark this manual.
      await patchAgenticUpdate(update.id, {
        status: "added",
        ledgerTxnIds: [],
        update,
      });
      reservedAdded = true;
      update.status = "added";

      const ledgerTxnIds = await applyAgenticUpdateToLedger(agenticUpdateToLedgerInput(update), {
        tradeDate,
        avgPrice,
      });
      ledgerWritten = true;

      await this.markUpdateHandled(update, "added", ledgerTxnIds);
      showToast(`Added ${updateKindLabel(update.kind)} to ledger.`, "success");
    } catch (err) {
      if (reservedAdded && !ledgerWritten) {
        try {
          await patchAgenticUpdate(update.id, {
            status: "pending",
            ledgerTxnIds: [],
            update,
          });
          update.status = "pending";
        } catch {
          /* ignore revert failure */
        }
        this.error = err instanceof Error ? err.message : "Failed to add update to ledger";
        showToast(this.error, "error");
      } else if (ledgerWritten) {
        update.status = "added";
        this.error = "";
        void this.refreshAfterLedgerWrite();
        showToast(`Added ${updateKindLabel(update.kind)} to ledger.`, "success");
      } else {
        this.error = err instanceof Error ? err.message : "Failed to add update to ledger";
        showToast(this.error, "error");
      }
    } finally {
      this.busyUpdateId = null;
      this.render();
    }
  }

  private persistCollapsibleFromDom(): void {
    if (!this.host) return;
    const pairs: Array<[string, string]> = [
      ["[data-agentic-snapshot-section]", "snapshot"],
      ["[data-agentic-reconcile-section]", "reconcile"],
      ["[data-agentic-updates-section]", "updates"],
    ];
    for (const [selector, sectionId] of pairs) {
      const details = this.host.querySelector<HTMLDetailsElement>(selector);
      if (!details) continue;
      setCollapsibleOpen(IMPORT_COLLAPSIBLE_SCOPE, sectionId, details.open);
      if (sectionId === "snapshot") this.snapshotSectionOpen = details.open;
      if (sectionId === "reconcile") this.reconcileSectionOpen = details.open;
      if (sectionId === "updates") this.updatesSectionOpen = details.open;
    }
  }

  private setPendingAuthUrl(url: string | null): void {
    this.pendingAuthUrl = url;
    writePendingAuthUrl(url);
  }

  private render(): void {
    if (!this.host) return;
    this.persistCollapsibleFromDom();
    this.snapshotSectionOpen = isCollapsibleOpen(IMPORT_COLLAPSIBLE_SCOPE, "snapshot", true);
    this.reconcileSectionOpen = isCollapsibleOpen(IMPORT_COLLAPSIBLE_SCOPE, "reconcile", true);
    this.updatesSectionOpen = isCollapsibleOpen(IMPORT_COLLAPSIBLE_SCOPE, "updates", true);
    const toolOptions = this.tools
      .map(
        (tool) =>
          `<option value="${esc(tool.name)}"${tool.name === this.selectedTool ? " selected" : ""}>${esc(tool.name)}</option>`,
      )
      .join("");
    const selected = this.tools.find((tool) => tool.name === this.selectedTool);
    const selectedSchema = selected?.inputSchema
      ? formatJson(selected.inputSchema)
      : '{\n  \n}';

    const connectDisabled =
      this.loading ||
      Boolean(this.oauthCooldown?.startBlocked) ||
      this.oauthCooldown?.canStart === false;
    const cooldownBanner =
      this.oauthCooldown?.message && (this.oauthCooldown.startBlocked || this.oauthCooldown.blocked)
        ? `<p class="sfe-agentic-cooldown muted small" role="status">${esc(this.oauthCooldown.message)}</p>`
        : "";

    const callbackDraft =
      this.host?.querySelector<HTMLTextAreaElement>("[data-agentic-callback]")?.value ?? "";
    const connectBlock = `
          <div class="sfe-agentic-oauth-pending panel">
            <p class="muted small"><strong>Step 1.</strong> Click Connect Robinhood, then Allow in the desktop browser.</p>
            ${
              this.pendingAuthUrl
                ? `<p class="row">
              <a class="small" href="${esc(this.pendingAuthUrl)}" target="_blank" rel="noopener noreferrer">Open Robinhood authorization</a>
            </p>`
                : ""
            }
            <p class="muted small"><strong>Step 2.</strong> After Allow, the browser goes to <code>http://127.0.0.1:33418/callback?code=…</code>. That page may look broken — that's expected.</p>
            <p class="muted small"><strong>Step 3.</strong> Copy the <em>full URL</em> from the address bar and paste it below, then Complete connection.</p>
            <label class="sfe-agentic-field">
              <span class="small muted">Callback URL</span>
              <textarea data-agentic-callback rows="3" spellcheck="false" placeholder="http://127.0.0.1:33418/callback?code=…&state=…">${esc(callbackDraft)}</textarea>
            </label>
            <div class="row">
              <button type="button" class="small" data-agentic-action="complete"${this.loading ? " disabled" : ""}>Complete connection</button>
              ${
                this.pendingAuthUrl
                  ? `<button type="button" class="small ghost" data-agentic-action="cancel-pending"${this.loading ? " disabled" : ""}>Cancel</button>`
                  : ""
              }
            </div>
          </div>`;

    const selectableAccounts = resolveSelectableAccounts(this.snapshot, this.accounts);
    this.selectedAccountNumber = normalizeSelectedAccount(
      selectableAccounts,
      this.selectedAccountNumber,
    );
    if (this.selectedAccountNumber) {
      storeAgenticAccount(this.selectedAccountNumber);
    }
    const tradingAccountSelector =
      selectableAccounts.length > 0
        ? renderTradingAccountSelector(selectableAccounts, this.selectedAccountNumber)
        : "";

    this.host.innerHTML = `
      <div class="sfe-agentic-panel panel">
        <div class="sfe-agentic-header">
          <div>
            <h2 class="insight-h">Import</h2>
            <p class="muted small sfe-agentic-lead">
              Robinhood Trading MCP — connect your account, then run tools from the proxy.
            </p>
          </div>
          <div class="row sfe-agentic-status-row">
            <span class="sfe-agentic-pill ${this.connected ? "connected" : "disconnected"}">
              ${this.connected ? "Robinhood connected" : "Not connected"}
            </span>
            ${
              this.connected
                ? `<button type="button" class="small ghost" data-agentic-action="disconnect">Disconnect</button>`
                : `<button type="button" class="small" data-agentic-action="connect"${connectDisabled ? " disabled" : ""}>Connect Robinhood</button>`
            }
            <button type="button" class="small ghost" data-agentic-action="refresh"${this.loading ? " disabled" : ""}>Refresh</button>
          </div>
          ${this.connected ? renderTokenExpiry(this.tokenExpiry) : ""}
        </div>

        ${this.error ? `<p class="sfe-agentic-error" role="alert">${esc(this.error)}</p>` : ""}
        ${this.connected ? "" : cooldownBanner}
        ${this.connected ? "" : connectBlock}

        ${tradingAccountSelector}

        ${renderReconciliationPanel(
          this.snapshot,
          this.sfQtyByTicker,
          this.sfOptionByContract,
          this.reconcileSectionOpen,
          this.selectedAccountNumber,
          this.selectedImportAccountId,
        )}

        ${renderUpdatesPanel({
          updates: this.updates,
          hasPreviousSnapshot: this.hasPreviousSnapshot,
          sectionOpen: this.updatesSectionOpen,
          expandedIds: this.expandedUpdateIds,
          addFormOpenId: this.addFormOpenId,
          ledgerByUpdateId: this.ledgerByUpdateId,
          ledgerHydratingIds: this.ledgerHydratingIds,
          defaultTradeDate: etTodayISO(),
          defaultAvgPriceFor: (update) => this.defaultAvgPriceForUpdate(update),
          busyUpdateId: this.busyUpdateId,
          pendingCount: this.updatesPendingCount,
          historyCount: this.updatesHistoryCount,
          settings: this.agenticSettings,
          snapshotRefreshedAt: this.snapshotRefreshedAt(),
          nextAutoRefreshSec: this.nextAutoRefreshSec,
          connected: this.connected,
          snapshotRefreshing: this.snapshotRefreshing,
          loading: !this.syncStatusLoaded,
          catalog: this.ledgerCatalog,
          selectedBrokerId: this.selectedImportBrokerId,
          selectedAccountId: this.selectedImportAccountId,
          loadError: this.updatesLoadError,
        })}

        ${renderSnapshotPanel(
          this.snapshot,
          this.connected,
          this.snapshotRefreshing,
          this.snapshotSectionOpen,
          this.selectedAccountNumber,
        )}

        ${
          this.loading
            ? `<p class="muted small">Loading Import…</p>`
            : this.connected
              ? `
          <div class="sfe-agentic-accounts-panel">
            <h2 class="insight-h">Live Robinhood accounts</h2>
            ${renderAccountsList(this.accounts)}
          </div>
          <div class="sfe-agentic-tools">
            <label class="sfe-agentic-field">
              <span class="small muted">Tool</span>
              <select data-agentic-tool-select>${toolOptions}</select>
            </label>
            ${
              selected?.description
                ? `<p class="muted small sfe-agentic-tool-desc">${esc(selected.description)}</p>`
                : ""
            }
            <label class="sfe-agentic-field">
              <span class="small muted">Arguments (JSON)</span>
              <textarea data-agentic-args rows="8" spellcheck="false">${esc(selectedSchema)}</textarea>
            </label>
            <div class="row">
              <button type="button" class="small" data-agentic-action="run">Run tool</button>
            </div>
            ${
              this.resultText
                ? `<pre class="sfe-agentic-result" data-agentic-result>${esc(this.resultText)}</pre>`
                : ""
            }
          </div>`
              : ""
        }
      </div>
    `;

    this.bindEvents();
    bindDetailsCollapsiblePersistence(this.host, IMPORT_COLLAPSIBLE_SCOPE);
  }

  private bindEvents(): void {
    if (!this.host) return;
    this.host.querySelector('[data-agentic-action="refresh"]')?.addEventListener("click", () => {
      void this.refresh(true);
    });
    this.host.querySelector<HTMLSelectElement>("[data-agentic-account-select]")?.addEventListener(
      "change",
      (event) => {
        const select = event.currentTarget as HTMLSelectElement;
        this.selectedAccountNumber = select.value || null;
        if (this.selectedAccountNumber) {
          storeAgenticAccount(this.selectedAccountNumber);
        }
        this.render();
      },
    );
    this.host.querySelector('[data-agentic-action="snapshot-refresh"]')?.addEventListener("click", () => {
      void this.refreshSnapshot();
    });
    this.host.querySelector('[data-agentic-action="connect"]')?.addEventListener("click", () => {
      void this.connect();
    });
    this.host.querySelector('[data-agentic-action="disconnect"]')?.addEventListener("click", () => {
      void this.disconnect();
    });
    this.host.querySelector('[data-agentic-action="run"]')?.addEventListener("click", () => {
      void this.runSelectedTool();
    });
    this.host.querySelector('[data-agentic-action="complete"]')?.addEventListener("click", () => {
      void this.completeCallback();
    });
    this.host.querySelector('[data-agentic-action="cancel-pending"]')?.addEventListener("click", () => {
      this.setPendingAuthUrl(null);
      this.error = "";
      this.render();
    });
    this.host.querySelector<HTMLSelectElement>("[data-agentic-tool-select]")?.addEventListener(
      "change",
      (event) => {
        const select = event.currentTarget as HTMLSelectElement;
        this.selectedTool = select.value;
        this.resultText = "";
        this.render();
      },
    );

    this.importSettingsCleanup?.();
    this.importSettingsCtx = {
      settings: this.agenticSettings,
      snapshotRefreshedAt: this.snapshotRefreshedAt(),
      nextAutoRefreshSec: this.nextAutoRefreshSec,
      loading: !this.syncStatusLoaded,
      catalog: this.ledgerCatalog,
      selectedBrokerId: this.selectedImportBrokerId,
      selectedAccountId: this.selectedImportAccountId,
    };
    this.importSettingsCleanup = bindImportSettings(this.host, { current: this.importSettingsCtx }, {
      onSaved: (settings) => {
        this.applyAgenticSettings(settings);
        this.syncAutoRefreshTimer();
        if (settings.autoImportEnabled) {
          void this.loadUpdates(true).then(() => this.render());
        }
      },
      onTargetChanged: (selection) => {
        this.selectedImportBrokerId = selection.brokerId;
        this.selectedImportAccountId = selection.accountId;
      },
      onError: (message) => {
        this.error = message;
        this.render();
      },
    });
  }

  private async refreshSnapshot(): Promise<void> {
    if (!this.connected) return;
    this.snapshotRefreshing = true;
    this.error = "";
    this.render();
    try {
      const response = await refreshAgenticSnapshot();
      this.snapshot = response.snapshot;
      this.applyAgenticSettings(response.settings);
      this.nextAutoRefreshSec = response.nextAutoRefreshSec ?? null;
      notifyAgenticSnapshotRefreshed(
        response.snapshot?.refreshedAt ?? response.snapshot?.capturedAt ?? null,
      );
      this.accounts = response.snapshot?.accounts ?? this.accounts;
      this.syncAutoRefreshTimer();
      await Promise.all([this.loadSfPositions(true), this.loadUpdates(true)]);
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to refresh snapshot";
    } finally {
      this.snapshotRefreshing = false;
      this.render();
    }
  }

  private async connect(): Promise<void> {
    this.loading = true;
    this.error = "";
    this.render();
    try {
      const result = await startAgenticOAuth(this.returnPath);
      if (result.connected) {
        this.setPendingAuthUrl(null);
        await this.refresh(true);
        return;
      }
      if (result.authorizationUrl) {
        this.setPendingAuthUrl(result.authorizationUrl);
        window.open(result.authorizationUrl, "_blank", "noopener,noreferrer");
        this.loading = false;
        this.render();
        return;
      }
      throw new Error("Robinhood did not return an authorization URL");
    } catch (err) {
      if (err instanceof AgenticOAuthCooldownError) {
        this.oauthCooldown = {
          blocked: true,
          startBlocked: true,
          canStart: false,
          retryAfterSec: err.retryAfterSec,
          startsRemaining: 0,
          cooldownMs: err.retryAfterSec * 1000,
          message: err.message,
        };
        this.syncCooldownTimer();
      }
      this.error = err instanceof Error ? err.message : "Failed to start Robinhood OAuth";
      this.loading = false;
      this.render();
    }
  }

  private async completeCallback(): Promise<void> {
    if (!this.host) return;
    const callbackUrl = this.host.querySelector<HTMLTextAreaElement>("[data-agentic-callback]")?.value?.trim();
    if (!callbackUrl) {
      this.error = "Paste the full callback URL from your browser address bar.";
      this.render();
      return;
    }
    this.loading = true;
    this.error = "";
    this.render();
    try {
      await completeAgenticOAuth(callbackUrl);
      this.setPendingAuthUrl(null);
      await this.refresh(true);
    } catch (err) {
      if (err instanceof AgenticOAuthCooldownError) {
        this.oauthCooldown = {
          blocked: true,
          startBlocked: false,
          canStart: false,
          retryAfterSec: err.retryAfterSec,
          startsRemaining: 0,
          cooldownMs: err.retryAfterSec * 1000,
          message: err.message,
        };
        this.syncCooldownTimer();
      }
      this.error = err instanceof Error ? err.message : "Failed to complete Robinhood OAuth";
      this.loading = false;
      this.render();
    }
  }

  private async disconnect(): Promise<void> {
    this.loading = true;
    this.error = "";
    this.render();
    try {
      await disconnectAgenticOAuth();
      this.connected = false;
      this.tools = [];
      this.accounts = [];
      this.tokenExpiry = null;
      this.clearTokenExpiryTimer();
      this.selectedTool = "";
      this.resultText = "";
      this.setPendingAuthUrl(null);
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Failed to disconnect Robinhood";
    } finally {
      this.loading = false;
      this.render();
      notifyRobinhoodStatusChanged();
    }
  }

  private async runSelectedTool(): Promise<void> {
    if (!this.host || !this.selectedTool) return;
    const argsEl = this.host.querySelector<HTMLTextAreaElement>("[data-agentic-args]");
    const rawArgs = argsEl?.value?.trim() || "{}";
    let args: Record<string, unknown> = {};
    try {
      const parsed = JSON.parse(rawArgs) as unknown;
      if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
        throw new Error("Arguments must be a JSON object");
      }
      args = parsed as Record<string, unknown>;
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Invalid JSON arguments";
      this.render();
      return;
    }

    this.loading = true;
    this.error = "";
    this.render();
    try {
      const result = await callAgenticTool(this.selectedTool, args);
      this.resultText = formatJson(result);
      await this.loadSnapshot();
    } catch (err) {
      this.error = err instanceof Error ? err.message : "Tool call failed";
    } finally {
      this.loading = false;
      this.render();
    }
  }
}
